"use client";
import { useEffect, useState, useMemo } from "react";
import {
  useDeactivatedUsersZaps,
  useEditorSpeculationRules,
  useOrganizationZaps,
  usePausedZaps,
  useSearchZaps,
  useStorageZap,
} from "@zapier/client";
import { PENDING_FOLDER_NAME } from "@zapier/utils";
import { SortDescriptor } from "@zapier/design-system";
import isNull from "lodash.isnull";
import {
  OrganizationZapFolderId,
  SearchZapsOrder,
  SearchZapsStatusWithAll,
} from "@zapier/bff";
import { parseMultiParameter, useRouterParams } from "@zapier/shared";

/** Hook for fetching hydrated Zaps to display in the main Zaps table.
 * This hook is specific to a view in the application and not for general use.
 */

interface Input {
  pageSize: number;
}

export default function useZapsList({ pageSize }: Input) {
  // read query params from URL when component mounts
  const {
    app,
    apps: appsRaw,
    appsOperator: appsOperatorRaw,
    connection,
    connections: connectionsRaw,
    lastRun,
    page,
    order,
    search,
    owner: selectedOwner,
    owners: ownersRaw,
    ownersOperator: ownersOperatorRaw,
    shared,
    status: selectedStatus,
    setParams,
    virtual,
    details,
  } = useRouterParams();

  const parsedOwners = parseMultiParameter(ownersRaw, ownersOperatorRaw);
  const owners =
    parsedOwners.values.length > 0 ? parsedOwners.values.join(",") : undefined;
  const ownersOperator = owners ? parsedOwners.operator : undefined;

  const parsedApps = parseMultiParameter(appsRaw, appsOperatorRaw);
  const apps =
    parsedApps.values.length > 0 ? parsedApps.values.join(",") : undefined;
  const appsOperator = apps ? parsedApps.operator : undefined;

  // Connection has a single operator; the parsed `.operator` is discarded.
  const parsedConnections = parseMultiParameter(connectionsRaw);
  const connections =
    parsedConnections.values.length > 0
      ? parsedConnections.values.join(",")
      : undefined;

  useEditorSpeculationRules();

  /** Owner selection *********************************************** */

  let owner = selectedOwner;
  // if viewing "shared with me" and there is no owner selected
  if (shared === "-me" && !selectedOwner) {
    owner = "-me";
    // if viewing "owned by me"
  } else if (shared === "me") {
    owner = "me";
  }

  /** Sorting *********************************************** */

  const sortChange = (descriptor: SortDescriptor) => {
    const orderChange = `${descriptor.direction === "ascending" ? "" : "-"}${descriptor.column}`;
    if (orderChange !== order) {
      setParams({ order: orderChange, page: 1 });
    }
  };

  /** Status *********************************************** */

  const status: SearchZapsStatusWithAll | undefined =
    selectedStatus === "all" || isNull(selectedStatus)
      ? undefined
      : (selectedStatus as SearchZapsStatusWithAll);

  /** App *************************************************** */

  let appId;
  if (app) {
    const [id] = app.split("::");
    appId = id;
  }

  /** Fetching Zaps *********************************************/

  const isDeactivatedFilter = shared === "deactivated";

  const {
    isFetching: isSearchZapsFetching,
    isLoading: isSearchZapsLoading,
    totalPages: totalSearchZapsPages,
    totalCount: totalSearchZapCount,
    zaps: searchZaps,
  } = useSearchZaps({
    enabled: !virtual && !details && !isDeactivatedFilter,
    lastRun: lastRun || undefined,
    order: order ? (order as SearchZapsOrder) : undefined,
    owner: owner || "",
    owners,
    ownersOperator,
    appId,
    apps,
    appsOperator,
    connection: connection ? Number(connection) : undefined,
    connections,
    page,
    pageSize,
    search: search || "",
    status,
  });

  const {
    isFetching: isDeactivatedZapsFetching,
    isLoading: isDeactivatedZapsLoading,
    totalPages: totalDeactivatedZapsPages,
    totalCount: totalDeactivatedZapCount,
    zaps: deactivatedZaps,
  } = useDeactivatedUsersZaps({
    enabled: isDeactivatedFilter && !virtual && !details,
    order: order ? (order as SearchZapsOrder) : undefined,
    appId,
    apps,
    appsOperator,
    connection: connection ? Number(connection) : undefined,
    connections,
    page,
    pageSize,
    search: search || "",
    status,
  });

  const {
    zaps: orgZaps,
    isLoading: isOrgZapsLoading,
    isFetching: isOrgZapsFetching,
    totalPages: totalOrgZapPages,
    totalCount: totalOrgZapCount,
  } = useOrganizationZaps({
    enabled: !!virtual && virtual !== PENDING_FOLDER_NAME && !details,
    folderId: (virtual as OrganizationZapFolderId) || undefined,
    page: page || 1,
  });

  // Fetch a single Zap if there is a details param
  const {
    zap: zapDetails,
    isLoading: isStorageZapLoading,
    isFetching: isStorageZapFetching,
  } = useStorageZap({
    id: Number(details) || undefined,
    enabled: !!details,
  });

  /** Loading state  *********************************************************
   * only show loading when filters change */

  const [didFiltersChange, setDidFiltersChange] = useState(true);

  useEffect(() => {
    setDidFiltersChange(true);
  }, [
    search,
    order,
    owner,
    owners,
    ownersOperator,
    shared,
    status,
    virtual,
    page,
    app,
    apps,
    appsOperator,
    connection,
    connections,
    lastRun,
  ]);

  useEffect(() => {
    if (
      !isSearchZapsFetching &&
      !isOrgZapsFetching &&
      !isStorageZapFetching &&
      !isDeactivatedZapsFetching
    ) {
      setDidFiltersChange(false);
    }
  }, [
    isSearchZapsFetching,
    isOrgZapsFetching,
    isStorageZapFetching,
    isDeactivatedZapsFetching,
  ]);

  const {
    isFetching: isPausedZapsFetching,
    isLoading: isPausedZapsLoading,
    totalPages: totalPausedZapPages,
    zaps: pausedZaps,
  } = usePausedZaps({
    enabled: !!virtual && virtual === PENDING_FOLDER_NAME,
  });

  const zaps = useMemo(() => {
    if (details) {
      return zapDetails ? [zapDetails] : [];
    }
    if (virtual === PENDING_FOLDER_NAME) {
      return pausedZaps;
    }
    if (isDeactivatedFilter) {
      return deactivatedZaps;
    }
    return virtual ? orgZaps : searchZaps;
  }, [
    virtual,
    orgZaps,
    pausedZaps,
    searchZaps,
    deactivatedZaps,
    isDeactivatedFilter,
    details,
    zapDetails,
  ]);

  const totalPages = () => {
    if (details) {
      return 1;
    }
    if (virtual === PENDING_FOLDER_NAME) {
      return totalPausedZapPages;
    }
    if (isDeactivatedFilter) {
      return totalDeactivatedZapsPages;
    }
    return virtual ? totalOrgZapPages : totalSearchZapsPages;
  };

  const totalResults = () => {
    if (details) {
      return zapDetails ? 1 : 0;
    }
    if (virtual === PENDING_FOLDER_NAME) {
      return pausedZaps.length;
    }
    if (isDeactivatedFilter) {
      return totalDeactivatedZapCount;
    }
    return virtual ? totalOrgZapCount : totalSearchZapCount;
  };

  const isLoading = () => {
    if (details) {
      return isStorageZapLoading;
    }
    if (virtual === PENDING_FOLDER_NAME) {
      return isPausedZapsLoading;
    }
    if (isDeactivatedFilter) {
      return isDeactivatedZapsLoading;
    }
    return virtual ? isOrgZapsLoading : isSearchZapsLoading;
  };

  const isFetching = () => {
    if (details) {
      return isStorageZapFetching;
    }
    if (virtual === PENDING_FOLDER_NAME) {
      return isPausedZapsFetching && didFiltersChange;
    }
    if (isDeactivatedFilter) {
      return isDeactivatedZapsFetching && didFiltersChange;
    }
    return virtual
      ? isOrgZapsFetching && didFiltersChange
      : isSearchZapsFetching && didFiltersChange;
  };

  return {
    isFetching: isFetching(),
    isLoading: isLoading(),
    order,
    page,
    sortChange,
    totalPages: totalPages(),
    totalResults: totalResults(),
    zaps,
  };
}
